import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Send } from 'lucide-react';
import { z } from 'zod';
import { FormDialog, FormSection, FormTextField } from '@/components/common/FormDialog';
import { TAKA_SYMBOL } from '@/constants/app.constants';
import { formatCount, formatNumber } from '@/utils/number';

const BATCH_NAME_MAX = 2000;

const batchNameFormSchema = z.object({
  batchName: z.string().max(BATCH_NAME_MAX),
});

type BatchNameFormValues = z.infer<typeof batchNameFormSchema>;

const EMPTY_BATCH_NAME: BatchNameFormValues = { batchName: '' };

interface PublishDonationBatchDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  pickedZonalManagerCount: number;
  pickedAmount: number;
  isPending: boolean;
  onPublish: (batchName: string | undefined) => void;
}

export function PublishDonationBatchDialog({
  open,
  onOpenChange,
  pickedZonalManagerCount,
  pickedAmount,
  isPending,
  onPublish,
}: PublishDonationBatchDialogProps) {
  const form = useForm<BatchNameFormValues>({
    resolver: zodResolver(batchNameFormSchema),
    defaultValues: EMPTY_BATCH_NAME,
  });

  useEffect(() => {
    if (open) form.reset(EMPTY_BATCH_NAME);
  }, [open, form]);

  const pickedSummary = `${formatCount(pickedZonalManagerCount, 'ZM')} - ${TAKA_SYMBOL} ${formatNumber(Math.round(pickedAmount))}`;

  function handlePublish(values: BatchNameFormValues) {
    const batchName = values.batchName.trim();
    onPublish(batchName === '' ? undefined : batchName);
  }

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      icon={Send}
      title="Publish Batch"
      description={pickedSummary}
      form={form}
      isPending={isPending}
      isEditMode={false}
      submitLabel="Publish"
      onSubmit={handlePublish}
    >
      <FormSection>
        <FormTextField
          control={form.control}
          name="batchName"
          label="Batch Name"
          placeholder="Optional, e.g. September-2026"
          isFullRow
        />
      </FormSection>
    </FormDialog>
  );
}
